import { cn } from "@/lib/cn";

interface MockingbirdLoaderProps {
  label?: string;
  detail?: string;
  compact?: boolean;
  inline?: boolean;
  screen?: boolean;
  progress?: boolean;
  className?: string;
}

export function MockingbirdLoader({
  label = "Mockingbird is flying...",
  detail,
  compact = false,
  inline = false,
  screen = false,
  progress = false,
  className,
}: MockingbirdLoaderProps) {
  const loader = (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex text-[color:var(--color-ink)]",
        inline ? "items-center gap-3 text-left" : "flex-col items-center justify-center gap-2 text-center",
        compact ? "text-sm" : "",
        screen && "w-full max-w-sm rounded-lg border border-[color:var(--color-rule)] bg-white px-6 py-5 shadow-[0_24px_70px_rgba(15,20,25,0.16)]",
        !screen && className,
      )}
    >
      <div
        className={cn(
          "relative shrink-0 overflow-hidden rounded-full border border-[color:var(--color-rule)] bg-white shadow-[0_12px_28px_rgba(15,20,25,0.08)]",
          compact ? "h-10 w-16" : "h-14 w-24",
        )}
      >
        <div className="mb-flight-path absolute left-1 right-1 top-1/2 h-px bg-[linear-gradient(90deg,transparent,var(--color-primary),transparent)] opacity-35" />
        <div className="mb-bird-flight absolute left-1 top-1/2 -translate-y-1/2 text-[color:var(--color-primary)]">
          <AnimatedMockingbird size={compact ? 38 : 48} />
        </div>
      </div>
      <div className={inline ? "min-w-0" : "w-full"}>
        <p className={cn("font-medium", inline && compact ? "truncate text-sm" : compact ? "text-sm" : "text-base")}>{label}</p>
        {detail && (
          <p className={cn("text-[color:var(--color-ink-mute)]", compact ? "text-xs" : "mt-1 text-sm")}>
            {detail}
          </p>
        )}
        {progress && (
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-[color:var(--color-rule)]/40">
            <div className="mb-progress-bar h-full w-1/3 bg-[color:var(--color-accent)]" />
          </div>
        )}
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );

  if (!screen) return loader;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[120] flex items-center justify-center bg-white/65 px-4 backdrop-blur-[2px]",
        className,
      )}
    >
      {loader}
    </div>
  );
}

function AnimatedMockingbird({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={Math.round(size * 0.68)}
      viewBox="0 0 96 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="overflow-visible"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="translate"
          values="2 0;-5 0;2 0"
          dur="0.38s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.3;0.85;0.3"
          dur="0.38s"
          repeatCount="indefinite"
        />
        <path d="M5 34H19" opacity="0.22" />
        <path d="M1 44H14" opacity="0.14" />
      </g>
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0 2;0 -3;0 2"
          dur="0.38s"
          repeatCount="indefinite"
        />
        <path
          d="M34 38C31 31 36 25 45 23C59 20 73 27 76 37C72 47 56 52 43 48C38 47 35 44 34 38Z"
          fill="white"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path
          d="M48 27C44 17 36 10 23 8C27 18 35 27 45 33"
          fill="white"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="18 46 31;-48 46 31;18 46 31"
            dur="0.38s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M51 29C53 17 63 10 78 8C75 19 65 29 53 36"
          fill="white"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            values="-18 52 33;46 52 33;-18 52 33"
            dur="0.38s"
            repeatCount="indefinite"
          />
        </path>
        <path
          d="M76 34L91 30L77 39"
          fill="var(--color-highlight)"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <circle cx="70" cy="33" r="2.4" fill="currentColor" />
        <path
          d="M34 42L18 49M36 47L24 58"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M44 45C51 47 60 45 66 39"
          stroke="var(--color-highlight)"
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.95"
        />
      </g>
    </svg>
  );
}
